import classNames from 'classnames';
import { testId } from '@jwp/ott-common/src/utils/common';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { getLocalStorageValue } from '@jwp/ott-common/src/constants';

import CollapsibleText from '../CollapsibleText/CollapsibleText';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';

import styles from './VideoDetails.module.scss';

type Props = {
  title: string;
  description: string;
  primaryMetadata: React.ReactNode;
  secondaryMetadata?: React.ReactNode;
  image?: string;
  startWatchingButton: React.ReactNode;
  shareButton: React.ReactNode;
  favoriteButton?: React.ReactNode;
  trailerButton?: React.ReactNode;
  children: React.ReactNode;
  descriptionEs: string;
  titleEs: string;
  titleEn?: string;
  descriptionEn?: string;
};

const VideoDetails: React.VFC<Props> = ({
  title,
  descriptionEs,
  description,
  primaryMetadata,
  secondaryMetadata,
  image,
  startWatchingButton,
  shareButton,
  favoriteButton,
  trailerButton,
  children,
  titleEs,
  descriptionEn,
  titleEn,
}) => {
  const breakpoint: Breakpoint = useBreakpoint();
  const LocalStorageValue = getLocalStorageValue();
  const { config } = useConfigStore.getState();
  const isMobile = breakpoint === Breakpoint.xs;
  const alt = ''; // intentionally empty for a11y, because adjacent text alternative
  const defaultLanguage = config?.custom?.defaultLanguage;
  const articleBackground = config?.custom?.articleBackgroundImageAlignment;

  let selectedTitle;
  let selectedDescription;
  if (defaultLanguage === 'es') {
    if (LocalStorageValue === 'es' || LocalStorageValue === undefined) {
      selectedTitle = title;
      selectedDescription = description;
    } else if (LocalStorageValue === 'en' && titleEn !== undefined) {
      selectedTitle = titleEn;
      selectedDescription = descriptionEn;
    } else {
      selectedTitle = title;
      selectedDescription = description;
    }
  } else if (defaultLanguage === 'en' || LocalStorageValue === 'en') {
    if (LocalStorageValue === 'en' || LocalStorageValue === undefined) {
      selectedTitle = title;
      selectedDescription = description;
    } else if (LocalStorageValue === 'es' || titleEs !== undefined) {
      selectedTitle = titleEs;
      selectedDescription = descriptionEs;
    } else {
      selectedTitle = title;
      selectedDescription = description;
    }
  } else {
    selectedTitle = title;
    selectedDescription = description;
  }

  const posterClass = classNames(styles.poster, {
    [styles['align-left']]: articleBackground === 'left',
    [styles['align-right']]: articleBackground === 'right',
    [styles['align-top']]: articleBackground === 'top',
    [styles['align-bottom']]: articleBackground === 'bottom',
    [styles['align-center']]: articleBackground === 'center',
  });
  return (
    <div data-testid={testId('cinema-layout')}>
      <header className={styles.video} data-testid={testId('video-details')} id="video-details">
        <div className={classNames(styles.main, styles.mainPadding)}>
          <img className={posterClass} src={image} alt={alt} />
          <div className={styles.posterFade} />
          <div className={styles.info}>
            <h1 className={styles.title}>{selectedTitle}</h1>
            <div className={styles.metaContainer}>
              <div className={styles.primaryMetadata}>{primaryMetadata}</div>
              {secondaryMetadata && <div className={styles.secondaryMetadata}>{secondaryMetadata}</div>}
            </div>
            <CollapsibleText text={selectedDescription as string} className={styles.description} maxHeight={isMobile ? 60 : 'none'} />
            <div className={styles.buttonBar}>
              {startWatchingButton}
              {trailerButton}
              {favoriteButton}
              {shareButton}
            </div>
          </div>
        </div>
      </header>
      {children}
    </div>
  );
};

export default VideoDetails;
